import styled from "@emotion/styled";
import { useHeadsUpClick } from "@/components/heads-up/context/heads-up-context";
import { SmartTimelineRow } from "@/components/heads-up/cards/common/smart-timeline-row";
import { SmartCardSection } from "@/components/heads-up/cards/common/layout";
import { CollapsibleSectionHeader } from "@/components/heads-up/cards/common/collapsible-section-header";
import { useIncrementalReveal } from "@/components/heads-up/cards/common/use-incremental-reveal";
import { useCallback, useMemo } from "react";
import type { FC, MouseEvent } from "react";
import type { Maybe } from "@/domains/common/types";
import type { EventTimelineSmartCard } from "@/components/heads-up/heads-up.types";
import { SeeMoreButton } from "@/components/heads-up/cards/common/see-more-button";
import type { SmartNotePreviewInfo } from "@/components/heads-up/cards/common/smart-note-preview.types";
import type {
  HeadsUpCardItemMeta,
  HeadsUpCardRendererAnalytics,
} from "@/components/heads-up/cards/registry";
import { HeadsUpItemSource } from "@/components/heads-up/analytics";

const EVENT_FALLBACK_TITLE = "Event";
const LINKED_NOTE_FALLBACK_TITLE = "Note";

type Props = {
  card: EventTimelineSmartCard;
  getNotePreviewInfo?: (noteId: string) => Maybe<SmartNotePreviewInfo>;
  analytics?: HeadsUpCardRendererAnalytics;
};

type TimelineItem = EventTimelineSmartCard["value"]["items"][number];

interface TimelineItemWithIndex {
  item: TimelineItem;
  positionIndex: number;
}

export const EventTimelineSmartCardRenderer: FC<Props> = ({
  card,
  getNotePreviewInfo,
  analytics,
}) => {
  const clickHandlers = useHeadsUpClick();

  const sectionTitle = card.value.title;
  const baseItemSource = card.originated_from_find_more
    ? HeadsUpItemSource.FindMore
    : HeadsUpItemSource.Standard;

  const sortedItems = useMemo<TimelineItemWithIndex[]>(
    () =>
      [...card.value.items]
        .sort((a, b) => Date.parse(b.associated_datetime) - Date.parse(a.associated_datetime))
        .map((item, positionIndex) => ({ item, positionIndex })),
    [card.value.items]
  );

  const buildAnalyticsMeta = useCallback(
    ({ item, positionIndex }: TimelineItemWithIndex): HeadsUpCardItemMeta => ({
      itemKind: "event",
      itemId: item.event_smart_entity_id,
      itemTitle: item.primary_label ?? EVENT_FALLBACK_TITLE,
      itemPositionIndex: positionIndex,
      itemSource: baseItemSource,
    }),
    [baseItemSource]
  );

  const { visibleItems, hasMore, showAll, reset } = useIncrementalReveal({
    cardId: card.id,
    items: sortedItems,
    initialCount: 4,
    step: 5,
  });

  return (
    <SmartCardSection>
      <CollapsibleSectionHeader title={sectionTitle} onCollapseChange={c => c && reset()}>
        <TimelineList>
          {visibleItems.map(({ item, positionIndex }, index) => {
            const primary = item.primary_label ?? EVENT_FALLBACK_TITLE;
            const linkedId = item.linked_note_id ?? undefined;
            const linkedPrimaryLabel = item.linked_note_primary_label ?? undefined;
            const notePreviewInfo = linkedId ? getNotePreviewInfo?.(linkedId) : undefined;
            const noteTitle = linkedPrimaryLabel ?? LINKED_NOTE_FALLBACK_TITLE;
            const analyticsMeta = buildAnalyticsMeta({ item, positionIndex });

            const handleClick = linkedId
              ? (event: MouseEvent<HTMLElement>) => {
                  analytics?.onItemClick?.(analyticsMeta);
                  const handler = clickHandlers?.noteClick(linkedId);
                  handler?.(event);
                }
              : undefined;

            const handlePreviewOpen = linkedId
              ? () => analytics?.onItemPreviewOpen?.(analyticsMeta)
              : undefined;

            return (
              <SmartTimelineRow
                key={item.event_smart_entity_id}
                noteId={linkedId}
                primary={primary}
                datetime={item.associated_datetime}
                onClick={handleClick}
                notePreviewInfo={notePreviewInfo}
                noteTitle={noteTitle}
                showTopConnector={index !== 0}
                showBottomConnector={positionIndex !== sortedItems.length - 1}
                onPreviewOpen={handlePreviewOpen}
              />
            );
          })}
          <SeeMoreButton hasMore={hasMore} onClick={showAll} />
        </TimelineList>
      </CollapsibleSectionHeader>
    </SmartCardSection>
  );
};

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2px;
`;
